import React, { useState } from 'react';
import clsx from 'clsx';
import { OptionField } from './OptionField';

// Base option item type that list logic operates on.
export interface OptionItem {
  id: string;
  text: string;
}

export interface DraggableOptionListProps<Item extends OptionItem = OptionItem> {
  items: Item[];
  defaultId?: string;
  defaultIds?: string[]; // For multi-select mode
  isMultiSelect?: boolean; // Enable multi-select mode
  className?: string;
  onChange?: (next: Item[]) => void; // Fired after a reorder completes
  onSetDefault?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onEdit?: (id: string, text: string) => void; // Inline edit of option text
  disableDelete?: (item: Item) => boolean; // Function to determine if delete should be disabled
  renderItem?: (item: Item, ctx: { isDefault: boolean; dragging: boolean }) => React.ReactNode;
}

export const DraggableOptionList = <Item extends OptionItem = OptionItem>({
  items,
  defaultId,
  defaultIds = [],
  isMultiSelect = false,
  className = '',
  onChange,
  onSetDefault,
  onDuplicate,
  onDelete,
  onEdit,
  disableDelete,
  renderItem,
}: DraggableOptionListProps<Item>) => {
  const [draggingId, setDraggingId] = useState<string | undefined>();
  const [dragOver, setDragOver] = useState<
    { id: string; placement: 'before' | 'after' } | undefined
  >();

  const clearDrag = () => {
    setDraggingId(undefined);
    setDragOver(undefined);
  };

  const commitReorder = (
    sourceId: string,
    targetId: string,
    placement: 'before' | 'after' = 'before',
  ) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const order = items.map((i) => i.id).filter((id) => id !== sourceId);
    const tgtIndex = order.indexOf(targetId);
    if (tgtIndex < 0) return;
    const insertIndex = placement === 'after' ? tgtIndex + 1 : tgtIndex;
    order.splice(insertIndex, 0, sourceId);
    const next = order.map((id) => items.find((i) => i.id === id)!) as Item[];
    onChange?.(next);
  };

  const placeholder = (targetId: string, placement: 'before' | 'after') => (
    <div
      key={`ph-${targetId}-${placement}`}
      className="h-9 w-full rounded-md border-2 border-dashed border-indigo-300 bg-indigo-50 opacity-50"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const sourceId = e.dataTransfer.getData('text/plain');
        if (sourceId) commitReorder(sourceId, targetId, placement);
        clearDrag();
      }}
    />
  );

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {items.map((item) => {
        // Support both single and multi-select default
        const isDefault = isMultiSelect ? defaultIds.includes(item.id) : item.id === defaultId;
        const isDraggingItem = draggingId === item.id;
        const showBefore =
          draggingId && dragOver?.id === item.id && dragOver.placement === 'before';
        const showAfter = draggingId && dragOver?.id === item.id && dragOver.placement === 'after';
        return (
          <React.Fragment key={item.id}>
            {showBefore && placeholder(item.id, 'before')}
            <div
              className={clsx('pr-1', isDraggingItem && 'opacity-70')}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', item.id);
                setDraggingId(item.id);
              }}
              onDragEnd={() => clearDrag()}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const placement: 'before' | 'after' =
                  e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                // Only update if changed to reduce jitter
                setDragOver((prev) =>
                  prev?.id === item.id && prev.placement === placement
                    ? prev
                    : { id: item.id, placement },
                );
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceId = e.dataTransfer.getData('text/plain');
                if (sourceId) commitReorder(sourceId, item.id, dragOver?.placement || 'before');
                clearDrag();
              }}
            >
              {renderItem ? (
                renderItem(item, { isDefault, dragging: isDraggingItem })
              ) : (
                <OptionField
                  id={item.id}
                  text={(item as OptionItem).text}
                  isDefault={isDefault}
                  onSetDefault={() => onSetDefault?.(item.id)}
                  onDuplicate={() => onDuplicate?.(item.id)}
                  onDelete={() => onDelete?.(item.id)}
                  onEditText={(id, nextText) => onEdit?.(id, nextText)}
                  disableDelete={disableDelete?.(item) ?? false}
                  draggable={false}
                />
              )}
            </div>
            {showAfter && placeholder(item.id, 'after')}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default DraggableOptionList;
